import type { SharedWindow } from './shareLayout';
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

// The window manager for the Desktop skin: a small store of floating windows, each owning a
// route path it renders in isolation. Open/close/focus/move/resize/minimize/maximize + z-order
// + tile/cascade. The layout is mirrored to sessionStorage (`hf.windows`) so a refresh
// restores it. No dependency — plain state + pointer events in Window.tsx drive it.

export interface WindowState {
  id: string;
  path: string;
  title: string;
  x: number;
  y: number;
  w: number;
  h: number;
  z: number;
  minimized: boolean;
  maximized: boolean;
  /** In-window navigation history (paths); `path` is always `history[index]`. */
  history?: string[];
  index?: number;
}

export interface WindowManager {
  windows: WindowState[];
  focusedId: string | null;
  open: (path: string, title?: string) => void;
  /** Navigate INSIDE a window (its routed content), leaving the base pane alone. */
  navigate: (id: string, path: string, opts?: { replace?: boolean }) => void;
  /** Step through a window's own history (−1 = back, +1 = forward). */
  go: (id: string, delta: number) => void;
  /** Retitle a window (the title bar follows the screen the window is showing). */
  setTitle: (id: string, title: string) => void;
  close: (id: string) => void;
  focus: (id: string) => void;
  move: (id: string, x: number, y: number) => void;
  resize: (id: string, w: number, h: number, x?: number, y?: number) => void;
  minimize: (id: string) => void;
  toggleMax: (id: string) => void;
  restore: (id: string) => void;
  minimizeAll: () => void;
  tile: () => void;
  cascade: () => void;
  /** Replace the open windows with a shared layout (`?desk=` link), fitted to this screen. */
  load: (layout: SharedWindow[]) => void;
}

const KEY = 'hf.windows';
const DEFAULT_W = 760;
const DEFAULT_H = 480;
const MIN_W = 320;
const MIN_H = 200;
const CASCADE = 28;

const Ctx = createContext<WindowManager | null>(null);

function viewport() {
  const w = typeof window !== 'undefined' ? window.innerWidth : 1280;
  const h = typeof window !== 'undefined' ? window.innerHeight : 800;
  return { w, h };
}

function readStored(): WindowState[] {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return [];
    const v = JSON.parse(raw);
    if (Array.isArray(v)) return v.filter((w) => w && typeof w.id === 'string' && typeof w.path === 'string');
  } catch {
    /* ignore */
  }
  return [];
}

let seq = 0;
const newId = () => `win-${Date.now().toString(36)}-${(seq++).toString(36)}`;

/**
 * Keep z compact: the stacking order is what matters, not the numbers. Renumbered 1..n by the
 * current order (ties by array position), with `top` (when given) moved to the very top. An
 * ever-growing counter would eventually climb over the overlay z-index ladder (Window.tsx).
 */
function restack(cur: WindowState[], top?: string): WindowState[] {
  const order = cur
    .map((w, i) => ({ w, i }))
    .sort((a, b) => (a.w.id === top ? 1 : b.w.id === top ? -1 : a.w.z - b.w.z || a.i - b.i))
    .map(({ w }) => w);
  const z = new Map(order.map((w, i) => [w.id, i + 1] as const));
  return cur.map((w) => (w.z === z.get(w.id) ? w : { ...w, z: z.get(w.id)! }));
}

export function WindowManagerProvider({ children }: { children: ReactNode }) {
  const [windows, setWindows] = useState<WindowState[]>(() => restack(readStored()));

  useEffect(() => {
    try {
      sessionStorage.setItem(KEY, JSON.stringify(windows));
    } catch {
      /* ignore */
    }
  }, [windows]);

  const focus = useCallback((id: string) => {
    setWindows((cur) => {
      const win = cur.find((w) => w.id === id);
      if (!win) return cur;
      // Already on top and visible: nothing to do (a no-op must not re-render every window).
      if (!win.minimized && cur.every((w) => w.id === id || w.z < win.z)) return cur;
      return restack(cur.map((w) => (w.id === id ? { ...w, minimized: false } : w)), id);
    });
  }, []);

  const open = useCallback((path: string, title?: string) => {
    setWindows((cur) => {
      const existing = cur.find((w) => w.path === path);
      if (existing) return restack(cur.map((w) => (w.id === existing.id ? { ...w, minimized: false } : w)), existing.id);
      const { w: vw, h: vh } = viewport();
      const n = cur.length;
      const x = Math.min(40 + n * CASCADE, Math.max(8, vw - DEFAULT_W - 8));
      const y = Math.min(48 + n * CASCADE, Math.max(8, vh - DEFAULT_H - 8));
      const id = newId();
      return restack([...cur, { id, path, title: title ?? path, x, y, w: Math.min(DEFAULT_W, vw - 16), h: Math.min(DEFAULT_H, vh - 120), z: n + 1, minimized: false, maximized: false, history: [path], index: 0 }], id);
    });
  }, []);

  const navigate = useCallback((id: string, path: string, opts?: { replace?: boolean }) => {
    setWindows((cur) =>
      cur.map((w) => {
        if (w.id !== id) return w;
        const history = w.history ?? [w.path];
        const index = w.index ?? history.length - 1;
        if (history[index] === path) return w;
        const next = opts?.replace ? [...history.slice(0, index), path] : [...history.slice(0, index + 1), path];
        return { ...w, path, history: next, index: next.length - 1 };
      }),
    );
  }, []);

  const go = useCallback((id: string, delta: number) => {
    setWindows((cur) =>
      cur.map((w) => {
        if (w.id !== id) return w;
        const history = w.history ?? [w.path];
        const index = Math.max(0, Math.min(history.length - 1, (w.index ?? history.length - 1) + delta));
        return { ...w, path: history[index]!, index };
      }),
    );
  }, []);

  // Returns the SAME array when nothing changes: a no-op must not re-render every window
  // (the title observer in Window.tsx would otherwise loop).
  const setTitle = useCallback((id: string, title: string) => setWindows((cur) => (cur.some((w) => w.id === id && w.title !== title) ? cur.map((w) => (w.id === id ? { ...w, title } : w)) : cur)), []);
  const close = useCallback((id: string) => setWindows((cur) => restack(cur.filter((w) => w.id !== id))), []);
  const move = useCallback((id: string, x: number, y: number) => setWindows((cur) => cur.map((w) => (w.id === id ? { ...w, x, y } : w))), []);
  const resize = useCallback(
    (id: string, w: number, h: number, x?: number, y?: number) =>
      setWindows((cur) => cur.map((win) => (win.id === id ? { ...win, w: Math.max(MIN_W, w), h: Math.max(MIN_H, h), ...(x != null ? { x } : {}), ...(y != null ? { y } : {}) } : win))),
    [],
  );
  const minimize = useCallback((id: string) => setWindows((cur) => cur.map((w) => (w.id === id ? { ...w, minimized: true } : w))), []);
  const toggleMax = useCallback((id: string) => {
    setWindows((cur) => restack(cur.map((w) => (w.id === id ? { ...w, maximized: !w.maximized, minimized: false } : w)), id));
  }, []);
  const restore = useCallback((id: string) => focus(id), [focus]);
  const minimizeAll = useCallback(() => setWindows((cur) => cur.map((w) => ({ ...w, minimized: true }))), []);

  const cascade = useCallback(() => {
    const { w: vw, h: vh } = viewport();
    setWindows((cur) =>
      cur.map((w, i) => ({ ...w, minimized: false, maximized: false, x: Math.min(40 + i * CASCADE, vw - MIN_W), y: Math.min(48 + i * CASCADE, vh - MIN_H), w: Math.min(DEFAULT_W, vw - 16), h: Math.min(DEFAULT_H, vh - 120) })),
    );
  }, []);

  const tile = useCallback(() => {
    setWindows((cur) => {
      const vis = cur.filter((w) => !w.minimized);
      if (!vis.length) return cur;
      const { w: vw, h: vh } = viewport();
      const top = 40; // below the menu bar
      const bottom = 40; // above the taskbar
      const cols = Math.ceil(Math.sqrt(vis.length));
      const rows = Math.ceil(vis.length / cols);
      const cw = Math.floor((vw - 8) / cols);
      const ch = Math.floor((vh - top - bottom) / rows);
      let i = 0;
      return cur.map((w) => {
        if (w.minimized) return w;
        const col = i % cols;
        const row = Math.floor(i / cols);
        i++;
        return { ...w, maximized: false, x: 4 + col * cw, y: top + row * ch, w: cw - 8, h: ch - 8 };
      });
    });
  }, []);

  const load = useCallback((layout: SharedWindow[]) => {
    const { w: vw, h: vh } = viewport();
    setWindows(
      restack(
        layout.map((l, i) => {
          const w = Math.max(MIN_W, Math.min(l.w, vw - 16));
          const h = Math.max(MIN_H, Math.min(l.h, vh - 64));
          return { id: newId(), path: l.path, title: l.title, x: Math.min(l.x, Math.max(0, vw - w)), y: Math.min(l.y, Math.max(0, vh - h)), w, h, z: i + 1, minimized: false, maximized: Boolean(l.max), history: [l.path], index: 0 };
        }),
      ),
    );
  }, []);

  const focusedId = useMemo(() => {
    const vis = windows.filter((w) => !w.minimized);
    if (!vis.length) return null;
    return vis.reduce((a, b) => (b.z > a.z ? b : a)).id;
  }, [windows]);

  const value = useMemo<WindowManager>(
    () => ({ windows, focusedId, open, navigate, go, setTitle, close, focus, move, resize, minimize, toggleMax, restore, minimizeAll, tile, cascade, load }),
    [windows, focusedId, open, navigate, go, setTitle, close, focus, move, resize, minimize, toggleMax, restore, minimizeAll, tile, cascade, load],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useWindows(): WindowManager {
  const c = useContext(Ctx);
  if (!c) throw new Error('useWindows requires <WindowManagerProvider>');
  return c;
}
